import NiceModal from "@ebay/nice-modal-react";
import clsx from "clsx";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AnimateHeight from "react-animate-height";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import {
  createSearchParams,
  NavLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { ReactComponent as CaretDownIcon } from "assets/icons/caret-down.svg";
import { ReactComponent as ChevronRightIcon } from "assets/icons/chevron-right.svg";
import { ReactComponent as LockIcon } from "assets/icons/lock.svg";
import { ReactComponent as NextIcon } from "assets/icons/next.svg";
import { ReactComponent as PrevIcon } from "assets/icons/prev.svg";
import { ReactComponent as TrashIcon } from "assets/icons/trash.svg";
import Box from "components/Box";
import Button from "components/Button";
import Chip from "components/Chip";
import FilesField from "components/FilesField";
import FileUrl from "components/FileUrl";
import ReportPart1Modal from "components/modals/ReportPart1Modal";
import SimpleModal from "components/modals/SimpleModal";
import TransferRequestModal from "components/modals/TransferRequestModal";
import Pagination from "components/Pagination";
import Select from "components/Select";
import SmartEditor from "components/SmartEditor";
import TitleHeading from "components/TitleHeading";
import TransferImages from "components/TransferImages";
import {
  useActivateQuestion,
  useCompleteInspection,
  useDeactivateQuestion,
  useDeleteAdvice,
  useDeleteAnswer,
  useGetQuestion,
  useGetQuestions,
  useGetQuestionStatus,
  useUpdateAnswer,
  useUpdateMonitoringStatus,
  useUpdateStatus,
  useWriteAdvice,
  useWriteAnswer,
} from "hooks/apis/question";
import { useGetStudent, useUpdateMemo } from "hooks/apis/student";
import useMaskUserId from "hooks/useMaskUserId";
import format from "lib/format";
import {
  loadingState,
  previousUrlState,
  tokenState,
  userState,
} from "store/store";
import {
  Answer,
  File as FileType,
  Question,
  SmartEditorType,
} from "types/types";
import {
  questionAnswerTypeOptions,
  questionDifficultyOptions,
} from "utils/const";
import { parseSearchString } from "utils/utils";

type FormValues = {
  title: string;
  responderName: string;
  classificationType: string;
  classificationDifficulty: string;
  files: File[];
  oldFiles?: FileType[];
};

const QnADetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const params = useParams();
  const methods = useForm<FormValues>({
    defaultValues: {
      responderName: "연구실",
    },
  });
  const contentRef = useRef<SmartEditorType>();
  const user = useRecoilValue(userState);

  const { data } = useGetQuestion({
    questionId: params.id,
    ...parseSearchString(location.search),
  });
  const writeAnswerMutation = useWriteAnswer();
  const updateAnswerMutation = useUpdateAnswer();
  const updateStatusMutation = useUpdateStatus();
  const updateMonitoringStatusMutation = useUpdateMonitoringStatus();

  useEffect(() => {
    if (data) {
      methods.setValue("title", `[답변]${data.title}`);
      const myAnswer = data.answers?.find(
        (answer) => answer.user?.id === user!.id,
      );
      if (myAnswer) {
        methods.reset({
          title: myAnswer.title,
          responderName: myAnswer.responderName,
          classificationDifficulty: myAnswer.classificationDifficulty,
          classificationType: myAnswer.classificationType,
          oldFiles: myAnswer.files,
        });
        contentRef.current?.setContent(myAnswer.content);
      }
    }
  }, [data]);

  const isTransfer = data
    ? !!data.transfer && ["대기중", "작성중"].includes(data.status)
    : pathname.includes("transfer");
  const isMonitoring = pathname.includes("monitoring");

  const title = useMemo(() => {
    if (isMonitoring) {
      return "조교 작업 모니터링";
    }
    if (isTransfer) {
      return "이관현황";
    }
    return "QnA현황";
  }, [isTransfer, isMonitoring]);

  useEffect(() => {
    if (isMonitoring) {
      if (data?.answers?.[0]?.monitoringStatus !== 2) {
        updateMonitoringStatusMutation.mutate({
          status: 1,
          questionBNo: params.id!,
        });

        return () => {
          updateMonitoringStatusMutation.mutate({
            status: 0,
            questionBNo: params.id!,
          });
        };
      }
    } else {
      if (!data?.answers?.length) {
        updateStatusMutation.mutate({ status: 1, questionBNo: params.id! });

        return () => {
          updateStatusMutation.mutate({ status: 0, questionBNo: params.id! });
        };
      }
    }
  }, [params.id, isMonitoring]);

  const token = useRecoilValue(tokenState);

  useEffect(() => {
    const handleTabClose = () => {
      if (isMonitoring && data?.answers?.[0]?.monitoringStatus !== 2) {
        fetch(
          `https://www.victoryqna.com/api/v1/questions/${params.id}/monitoring/status`,
          {
            keepalive: true,
            method: "post",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token || ""}`,
            },
            body: JSON.stringify({ status: 0 }),
          },
        );
      } else {
        if (!data?.answers?.length) {
          fetch(
            `https://www.victoryqna.com/api/v1/questions/${params.id}/status`,
            {
              keepalive: true,
              method: "post",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token || ""}`,
              },
              body: JSON.stringify({ status: 0 }),
            },
          );
        }
      }
    };

    window.addEventListener("beforeunload", handleTabClose);

    return () => {
      window.removeEventListener("beforeunload", handleTabClose);
    };
  }, [isMonitoring, params.id, data?.answers, token]);

  const titleDescription = useMemo(() => {
    if (isMonitoring) {
      return "조교 작업 현황 모니터링 화면입니다.";
    }
    if (isTransfer) {
      return "조교가 이관한 질문 화면입니다.";
    }
    return "질문 및 답변 현황 화면입니다.";
  }, [isTransfer, isMonitoring]);

  const isAnswered = useMemo(
    () =>
      !["대기중", "작성중"].includes(data?.status ?? "") &&
      !data?.answers?.find((answer) => answer.user?.id === user!.id),
    [data, user],
  );

  const onPrev = () => {
    const prev = () => {
      if (!data?.prevbNo) {
        return;
      }

      const path = pathname.substring(0, pathname.lastIndexOf("/"));

      navigate({
        pathname: `${path}/${data.prevbNo}`,
        search: location.search,
      });
    };

    if (isMonitoring) {
      updateMonitoringStatusMutation.mutate(
        { status: 0, questionBNo: params.id! },
        { onSuccess: prev },
      );
    } else {
      updateStatusMutation.mutate(
        { status: 0, questionBNo: params.id! },
        { onSuccess: prev },
      );
    }
  };

  const onNext = useCallback(() => {
    const next = () => {
      if (!data) {
        return;
      }

      if (!data.nextbNo) {
        NiceModal.show(SimpleModal, {
          title: "QnA현황",
          message: "다음 질문이 더 이상 없습니다.",
          showCancel: false,
        });
        return;
      }

      const path = pathname.substring(0, pathname.lastIndexOf("/"));

      navigate({
        pathname: `${path}/${data.nextbNo}`,
        search: location.search,
      });
    };
    if (isMonitoring) {
      if (data?.answers?.[0]?.monitoringStatus !== 2) {
        updateMonitoringStatusMutation.mutate(
          { status: 0, questionBNo: params.id!, showLoading: true },
          { onSuccess: next },
        );
      } else {
        next();
      }
    } else {
      if (!data?.answers?.length) {
        updateStatusMutation.mutate(
          { status: 0, questionBNo: params.id!, showLoading: true },
          { onSuccess: next },
        );
      } else {
        next();
      }
    }
  }, [data, pathname, location.search, isMonitoring, params.id]);

  if (!data) {
    return (
      <main className="p-[1.875rem] pl-[15rem] 1350:pl-[16rem] 1400:pl-[16.875rem] w-full flex gap-[0.313rem]">
        <div className="flex-1">
          <div className="flex items-end justify-between mb-[0.625rem]">
            <TitleHeading title={title} description={titleDescription} />
            {params.id && (
              <div className="flex gap-[1.875rem]">
                <button type="button">
                  <PrevIcon className="size-[2.25rem]" />
                </button>
                <button type="button">
                  <NextIcon className="size-[2.25rem]" />
                </button>
              </div>
            )}
          </div>
          <QnaContent />
        </div>
        <div className="sticky overflow-auto top-[9.563rem] h-[calc(100vh-11.438rem)] flex flex-col w-[18rem] 1400:w-[19rem] 1500:w-[21.875rem] 1700:w-[31.813rem] space-y-[0.438rem]">
          <Box className="w-full space-y-5">
            <h4 className="font-medium text-18">학생 정보가 없습니다.</h4>
            <dl className="grid grid-cols-[5.313rem_1fr] gap-y-[0.625rem] text-14">
              <dt className="text-secondary">학년</dt>
              <dd>-</dd>
              <dt className="text-secondary">계열</dt>
              <dd>-</dd>
              <dt className="text-secondary">과목등급 </dt>
              <dd>-</dd>
              <dt className="text-secondary">메모</dt>
              <dd className="inline-flex">
                <div className="h-[3.563rem] flex border border-[transparent]">
                  -
                </div>
              </dd>
            </dl>
          </Box>
          {(["대기중", "작성중"].includes(
            parseSearchString(location.search).status,
          ) ||
            !params.id) && (
            <Box className="flex-1 overflow-hidden flex flex-col !p-0">
              <QnaList />
            </Box>
          )}
        </div>
      </main>
    );
  }

  const Main = isAnswered ? "main" : "form";

  const onAnswer = (form: FormValues) => {
    const content = contentRef.current!.getContent() as string;
    if (!content || content === "<p><br></p>") {
      contentRef.current!.focus();
      return;
    }

    const myAnswer = data!.answers?.find(
      (answer) => answer.user?.id === user!.id,
    );

    if (myAnswer) {
      updateAnswerMutation.mutate({
        ...form,
        questionBNo: data!.bNo,
        content,
      });
    } else {
      NiceModal.show(SimpleModal, {
        title: "답변 등록",
        message: "답변 등록 시 수정 및 삭제가 어렵습니다.\n등록 하시겠습니까?",
        submitText: "등록",
      })
        .then(() => {
          writeAnswerMutation.mutate(
            {
              ...form,
              questionBNo: data!.bNo,
              content,
            },
            {
              onSuccess: () => {
                NiceModal.show(SimpleModal, {
                  title: "QnA현황",
                  message: "답변이 등록되었습니다.",
                  submitText: "다음 질문으로 이동",
                  cancelText: "확인",
                })
                  .then(onNext)
                  .catch(() => {});
              },
            },
          );
        })
        .catch(() => {});
    }
  };

  return (
    <FormProvider {...methods}>
      <Main
        {...(isAnswered ? {} : { onSubmit: methods.handleSubmit(onAnswer) })}
        className="p-[1.875rem] pl-[15rem] 1350:pl-[16rem] 1400:pl-[16.875rem] w-full flex gap-[0.313rem]"
      >
        <div className="flex-1">
          <div className="flex items-end justify-between mb-[0.625rem]">
            <TitleHeading title={title} description={titleDescription} />
            <div className="flex gap-[1.875rem]">
              <button type="button" onClick={onPrev} disabled={!data?.prevbNo}>
                <PrevIcon className="size-[2.25rem]" />
              </button>
              <button type="button" onClick={onNext} disabled={!data?.nextbNo}>
                <NextIcon className="size-[2.25rem]" />
              </button>
            </div>
          </div>
          {data ? <QnaContent {...data} /> : <QnaContent />}
          {data?.answers?.length ? (
            data.answers.map((answer) => (
              <QnaAnswer
                key={answer.id}
                {...answer}
                // @ts-expect-error
                ref={answer.user?.id === user!.id ? contentRef : undefined}
              />
            ))
          ) : (
            // @ts-expect-error
            <QnaAnswer ref={contentRef} />
          )}
          <StudentQuestions />
        </div>
        <QnaSide
          {...(data || {})}
          isMonitoring={isMonitoring}
          isTransfer={isTransfer}
          onNext={onNext}
        />
      </Main>
    </FormProvider>
  );
};

const QnaContent = memo((data: Question | Record<string, never>) => {
  const location = useLocation();
  const isLoading = useRecoilValue(loadingState);

  const getSectionTitle = () => {
    const params = new URLSearchParams(location.search);
    // @ts-expect-error
    const mine = params.get("mine") ? +params.get("mine") : 0;
    if (mine) {
      return "내 답변";
    }

    // @ts-expect-error
    const transfer = params.get("transfer") ? +params.get("transfer") : 0;
    if (transfer) {
      return "대기중";
    }

    // @ts-expect-error
    const monitoring = params.get("monitoring") ? +params.get("monitoring") : 0;
    if (monitoring) {
      return params.get("status") === "완료" ? "검수 완료" : "대기중";
    }

    return params.get("status");
  };

  const hasData = "title" in data;

  return (
    <>
      <Box className="flex justify-between items-center mb-[0.313rem] h-14">
        {hasData ? (
          <>
            <div className="flex items-center text-18 font-medium">
              <h3 className="text-18 font-medium">{getSectionTitle()}</h3>
              <ChevronRightIcon className="size-[2.125rem]" />
              {data.secret && <LockIcon className="size-6" />}
              <p>{data.title}</p>
            </div>
            <span className="text-14 text-secondary">
              작성일 : {format(data.registrationDate, "yyyy/MM/dd HH:mm:ss")}
            </span>
          </>
        ) : (
          !isLoading && (
            <p className="text-18 font-medium text-secondary">
              <strong className="text-primary font-medium">
                ✅ 우측 질문 리스트 영역에서 질문을 선택해주세요.
              </strong>{" "}
              (답변을 완료한 경우 새로운 질문이 노출됩니다.)
            </p>
          )
        )}
      </Box>
      <table className="w-full table-fixed border border-gray bg-white">
        <tbody>
          <tr className="h-14 border-b border-gray">
            <th
              scope="row"
              className="border-r border-gray w-[7rem] px-6 text-left bg-lightGray"
            >
              질문 유형
            </th>
            <td className="px-[1.875rem] w-[7.5rem] 1400:w-[8.125rem] 1500:w-[9.375rem] 1600:w-[10.938rem]">
              {hasData ? (
                <p className="line-clamp-1">{data.questionType}</p>
              ) : (
                "-"
              )}
            </td>
            <th
              scope="row"
              className="border-r border-gray w-[8.875rem] px-6 text-left bg-lightGray"
            >
              강좌수강유/무
            </th>
            <td className="px-[1.875rem] w-[9.375rem] 1600:w-[10.938rem]">
              <p className="line-clamp-1">
                {hasData ? data.courseAttendance : "-"}
              </p>
            </td>
            <th
              scope="row"
              className="border-r border-gray w-[7rem] px-6 text-left bg-lightGray"
            >
              첨부파일
            </th>
            <td className="px-[1.875rem]">
              {hasData
                ? !!data.files.length &&
                  data.files.map((file) => (
                    <FileUrl key={file.fileno} file={file} files={data.files} />
                  ))
                : "-"}
            </td>
          </tr>
          <tr className="border-b border-gray">
            <th
              scope="row"
              className="border-r border-gray pt-4 pb-[1.094rem] w-[7rem] pl-6 text-left align-top bg-lightGray"
            >
              강좌/강의
            </th>
            <td colSpan={5} className="px-[1.875rem] py-4">
              {hasData ? (
                <>
                  {data.courseName} {data.videoLink ? "동영상 위치" : ""}{" "}
                  {data.videoTimestamp && (
                    <a
                      href={data.videoLink}
                      target="_blank noreferrer"
                      className="text-primary"
                    >
                      {data.videoTimestamp}
                    </a>
                  )}
                </>
              ) : (
                "-"
              )}
            </td>
          </tr>
          <tr className="border-b border-gray">
            <th
              scope="row"
              className="border-r border-gray pt-4 pb-[1.094rem] w-[7rem] px-6 text-left align-top bg-lightGray"
            >
              교재
            </th>
            <td colSpan={5} className="px-[1.875rem] py-4">
              {hasData
                ? data.textbookName
                  ? `교재명 :${data.textbookName} 페이지수 :${data.textbookPage} 문제번호 :${data.problemNo ?? "미 기입"}`
                  : ""
                : "-"}
            </td>
          </tr>
          <tr>
            <th
              scope="row"
              className="border-r border-gray pt-4 pb-[1.094rem] w-[7rem] px-6 text-left align-top bg-lightGray"
            >
              질문 내용
            </th>
            <td colSpan={5} className="px-[1.875rem] py-4 whitespace-pre-wrap">
              <div
                className={clsx({ "min-h-[12.5rem]": !data.questionContent })}
              >
                {hasData ? (
                  <div
                    className="leading-normal"
                    dangerouslySetInnerHTML={{
                      __html: data.questionContent || "",
                    }}
                  />
                ) : (
                  "-"
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
});

QnaContent.displayName = "QnaContent";

const QnaAnswer = forwardRef<SmartEditorType, Answer | Record<string, never>>(
  (answer, ref) => {
    const { register } = useFormContext();
    const user = useRecoilValue(userState);
    const params = useParams();
    const pathname = useLocation().pathname;
    const isMonitoring = pathname.includes("monitoring");
    const maskUserId = useMaskUserId();

    const notMyAnswer =
      ("id" in answer && answer.user?.id !== user!.id) || isMonitoring;

    return (
      <>
        <Box className="flex justify-between mt-[0.625rem] mb-[0.313rem]">
          <h3 className="text-18 font-medium">
            {notMyAnswer ? "답변" : "답변 작성"}
          </h3>
          {answer.registrationDate && (
            <span className="text-14 text-secondary">
              등록일자 :{" "}
              {format(answer.registrationDate, "yyyy/MM/dd HH:mm:ss")}
            </span>
          )}
        </Box>
        <table className="table-fixed w-full border border-gray">
          <tbody>
            <tr className="border-b border-gray">
              <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                제목
              </th>
              <td
                className={clsx("bg-white pl-[1.875rem] pr-5", {
                  "!bg-[#EDEDED]": answer.status === "재답변",
                  "!bg-[#E5EAFF]":
                    pathname.includes("monitoring") &&
                    answer.user?.grade.name === "Level 0",
                })}
              >
                {notMyAnswer ? (
                  answer.title
                ) : (
                  <input
                    {...register("title", { required: true })}
                    type="text"
                    className="h-9 w-full rounded-md border border-secondary px-3 outline-none"
                  />
                )}
              </td>
            </tr>
            <tr className="border-b border-gray">
              <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                답변자
              </th>
              <td
                className={clsx("bg-white pl-[1.875rem] pr-5", {
                  "!bg-[#EDEDED]": answer.status === "재답변",
                  "!bg-[#E5EAFF]":
                    pathname.includes("monitoring") &&
                    answer.user?.grade.name === "Level 0",
                })}
              >
                {notMyAnswer ? (
                  <>
                    {`${answer.responderName ?? answer.user?.name}${answer.responderId ? `(${answer.responderId})` : `${answer.user?.userId ? `(${maskUserId(answer.user.userId)})` : ""}`}${answer.user ? " / " : ""}`}
                    {answer.user && [1, 2].includes(user!.grade.id) && (
                      <strong className="font-normal text-primary">
                        {answer.user.grade?.name}
                      </strong>
                    )}
                  </>
                ) : (
                  <input
                    {...register("responderName", { required: true })}
                    type="text"
                    className="h-9 w-full rounded-md border border-secondary px-3 outline-none"
                  />
                )}
              </td>
            </tr>
            <tr className="border-b border-gray">
              <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                질문 분류
              </th>
              <td
                className={clsx("bg-white px-[1.875rem]", {
                  "!bg-[#EDEDED]": answer.status === "재답변",
                  "!bg-[#E5EAFF]":
                    pathname.includes("monitoring") &&
                    answer.user?.grade.name === "Level 0",
                })}
              >
                <div className="flex gap-[0.625rem]">
                  {notMyAnswer ? (
                    <>
                      {answer.classificationType
                        ? `${answer.classificationType} / `
                        : ""}
                      {answer.classificationDifficulty}
                    </>
                  ) : (
                    <>
                      <Select
                        name="classificationType"
                        required
                        options={questionAnswerTypeOptions}
                        border
                        className="!w-[9.375rem]"
                      />
                      <Select
                        name="classificationDifficulty"
                        required
                        options={questionDifficultyOptions}
                        border
                        className="!w-[9.375rem]"
                      />
                    </>
                  )}
                </div>
              </td>
            </tr>
            <tr className="border-b border-gray">
              <th className="w-[7rem] align-top pt-[0.938rem] bg-lightGray px-6 text-left text-16">
                답변 내용
              </th>
              <td
                className={clsx(
                  "bg-white pl-[1.875rem] pr-5 pb-6 pt-[0.625rem] whitespace-pre-wrap",
                  {
                    "!bg-[#EDEDED]": answer.status === "재답변",
                    "!bg-[#E5EAFF]":
                      pathname.includes("monitoring") &&
                      answer.user?.grade.name === "Level 0",
                  },
                )}
              >
                {notMyAnswer ? (
                  <div
                    className="qna-answer leading-normal"
                    dangerouslySetInnerHTML={{
                      __html: answer.content || "",
                    }}
                  />
                ) : (
                  <>
                    <SmartEditor ref={ref} key={params.id} />
                  </>
                )}
              </td>
            </tr>
            <tr>
              <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                첨부파일
              </th>
              <td
                className={clsx("bg-white px-[1.875rem]", {
                  "!bg-[#EDEDED]": answer.status === "재답변",
                  "!bg-[#E5EAFF]":
                    pathname.includes("monitoring") &&
                    answer.user?.grade.name === "Level 0",
                })}
              >
                {notMyAnswer ? (
                  answer.files?.map((file) => (
                    <FileUrl
                      key={file.fileno}
                      file={file}
                      files={answer.files}
                    />
                  ))
                ) : (
                  <FilesField />
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </>
    );
  },
);

QnaAnswer.displayName = "QnaAnswer";

const QnaList = () => {
  const params = useParams();
  const { data } = useGetQuestions({
    status: "대기중",
    questionStatus: "대기중",
    size: 10,
    mine: 0,
    transfer: 0,
    page: 1,
    activeBNo: params.id,
    sort: "createdAsc",
  });
  const getQuestionStatusMutation = useGetQuestionStatus();
  const navigate = useNavigate();

  const handleQuestionClick = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    questionBNo: string,
  ) => {
    e.preventDefault();
    if (questionBNo === params.id) {
      return;
    }

    const data = await getQuestionStatusMutation.mutateAsync({
      questionBNo,
      activeBNo: params.id ?? "",
    });

    if (data.status === "작성중") {
      NiceModal.show(SimpleModal, {
        title: "작성중인 질문",
        message:
          "이미 다른 조교가 작성중인 질문입니다.\n다른 질문을 선택해주세요.",
        showCancel: false,
      });
    } else {
      navigate({
        pathname: `/qna/${questionBNo}`,
        search: `?${createSearchParams(
          JSON.parse(
            JSON.stringify({
              status: "대기중",
              questionStatus: "대기중",
              size: 10,
              mine: 0,
              transfer: 0,
              sort: "createdAsc",
            }),
          ),
        )}`,
      });
    }
  };

  return (
    <Box className="flex-1 overflow-hidden flex flex-col !p-0">
      <h4 className="font-medium text-18 px-5 pt-[1.063rem]">질문 리스트</h4>
      <div className="flex-1 overflow-hidden pt-[1.063rem] flex flex-col">
        <ul className="flex-1 overflow-auto text-14 text-secondary font-medium space-y-[0.625rem] px-[0.875rem] pb-[1.063rem]">
          {data?.length ? (
            data.map((question) => (
              <NavLink
                onClick={(e) => handleQuestionClick(e, question.bNo)}
                key={question.bNo}
                to={{
                  pathname: `/qna/${question.bNo}`,
                  search: `?${createSearchParams(
                    JSON.parse(
                      JSON.stringify({
                        status: "대기중",
                        questionStatus: "대기중",
                        size: 10,
                        mine: 0,
                        transfer: 0,
                        sort: "createdAsc",
                      }),
                    ),
                  )}`,
                }}
                className={({ isActive }) =>
                  clsx(
                    "relative pl-[0.563rem] pr-[0.375rem] h-[4.5rem] flex bg-[#FCFCFC] border border-[#EEEEEE] rounded-lg items-center hover:shadow-[0px_4px_4px_0px_#00000040] hover:after:border-2 hover:after:absolute hover:after:inset-[-1px] hover:after:border-primary hover:after:rounded-lg duration-150",
                    {
                      "shadow-[0px_4px_4px_0px_#00000040] after:border-2 after:absolute after:inset-[-1px] after:border-primary after:rounded-lg":
                        isActive,
                    },
                  )
                }
              >
                {question.secret && <LockIcon className="size-6 shrink-0" />}
                <p className="text-left line-clamp-1 break-all">
                  {question.title}
                </p>
              </NavLink>
            ))
          ) : (
            <p className="text-14 text-secondary p-5">
              작성된 기록이 없습니다.
            </p>
          )}
        </ul>
      </div>
    </Box>
  );
};

const QnaSide = ({
  isMonitoring,
  isTransfer,
  onNext,
  ...data
}: Question & {
  isMonitoring: boolean;
  isTransfer: boolean;
  onNext: () => void;
}) => {
  const user = useRecoilValue(userState);

  const { register, getValues, watch, setValue } = useForm<{
    memo: string;
    advice?: string;
  }>();
  const writeAdviceMutation = useWriteAdvice();
  const deleteAdviceMutation = useDeleteAdvice();
  const updateMemoMutation = useUpdateMemo();
  const completeInspectionMutation = useCompleteInspection();
  const deleteAnswerMutation = useDeleteAnswer();
  const deactivateQuestionMutation = useDeactivateQuestion();
  const activateQuestionMutation = useActivateQuestion();

  const previousUrl = useRecoilValue(previousUrlState);
  const navigate = useNavigate();
  const token = useRecoilValue(tokenState);
  const location = useLocation();
  const pathname = location.pathname;

  const advice = watch("advice");

  const myAnswer = useMemo(
    () => data.answers?.find((answer) => answer.user?.id === user!.id),
    [data, user],
  );

  const { data: student } = useGetStudent({
    studentId: data.student?.id,
  });

  useEffect(() => {
    if (student) {
      setValue("memo", student.memo);
    }
  }, [student]);

  useEffect(() => {
    const handleTabClose = () => {
      const memo = getValues("memo");
      if (student && memo !== student?.memo) {
        fetch(
          `https://www.victoryqna.com/api/v1/students/${data.student!.id}/memo`,
          {
            keepalive: true,
            method: "post",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token || ""}`,
            },
            body: JSON.stringify({ memo, id: data.student!.id }),
          },
        );
      }
    };

    window.addEventListener("beforeunload", handleTabClose);

    return () => {
      const memo = getValues("memo");
      if (student && memo !== student?.memo) {
        updateMemoMutation.mutate({ memo, id: data.student!.id });
      }
      window.removeEventListener("beforeunload", handleTabClose);
    };
  }, []);

  const requestTransfer = () => {
    NiceModal.show(TransferRequestModal, {
      questionBNo: data.bNo,
      title: data.title,
    })
      .then(() => {
        NiceModal.show(SimpleModal, {
          title: "이관현황",
          message: "이관 완료되었습니다.",
          submitText: "다음 질문으로 이동",
          cancelText: "확인",
        })
          .then(onNext)
          .catch(() => {});
      })
      .catch(() => {});
  };

  const report = () => {
    NiceModal.show(ReportPart1Modal, data);
  };

  const registration = register("memo");

  const onBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    registration.onBlur(e);
    const memo = getValues("memo");
    if (student && memo !== student?.memo) {
      updateMemoMutation.mutate({ memo, id: data.student!.id });
    }
  };

  const onWriteAdvice = () => {
    const advice = getValues("advice");
    if (advice) {
      writeAdviceMutation.mutate(
        { content: advice, questionBNo: data.bNo },
        {
          onSuccess: () => {
            setValue("advice", "");
          },
        },
      );
    }
  };

  const deleteAdvice = (id: number) => {
    NiceModal.show(SimpleModal, {
      title: "조언 삭제",
      message: "조언 기록을 삭제하시겠습니까?",
      submitText: "삭제",
    })
      .then(() => {
        deleteAdviceMutation.mutate({ id, questionBNo: data.bNo });
      })
      .catch(() => {});
  };

  const onCompleteInspection = () => {
    completeInspectionMutation.mutate(
      { questionBNo: data.bNo, status: 1 },
      {
        onSuccess: () => {
          NiceModal.show(SimpleModal, {
            title: "검수 완료",
            message: "검수 완료되었습니다.",
            submitText: "다음 질문으로 이동",
            cancelText: "확인",
          })
            .then(onNext)
            .catch(() => {});
        },
      },
    );
  };

  const onCancelInspection = () => {
    completeInspectionMutation.mutate(
      { questionBNo: data.bNo, status: 0 },
      {
        onSuccess: () => {
          NiceModal.show(SimpleModal, {
            title: "검수 취소",
            message: "검수 취소가 완료되었습니다.",
            submitText: "다음 질문으로 이동",
            cancelText: "확인",
          })
            .then(onNext)
            .catch(() => {});
        },
      },
    );
  };

  const onBack = () => {
    if (previousUrl && !/\d/.test(previousUrl)) {
      navigate(-1);
    } else {
      const statusPath: Record<string, string> = {
        대기중: "/ongoing",
        "답변 완료": "/completed",
      };
      const params = new URLSearchParams(location.search);
      const status = params.get("status");
      // @ts-expect-error
      const mine = params.get("mine") ? +params.get("mine") : 0;
      const path =
        pathname.substring(0, pathname.lastIndexOf("/")) + mine
          ? "/mine"
          : (statusPath[status ?? ""] ?? "");

      navigate({
        pathname: path,
        search: location.search,
      });
    }
  };

  const onDelete = () => {
    deleteAnswerMutation.mutate({ questionBNo: data.bNo });
  };

  const onDeactivate = () => {
    deactivateQuestionMutation.mutate({ questionBNo: data.bNo });
  };

  const onActivate = () => {
    activateQuestionMutation.mutate({ questionBNo: data.bNo });
  };

  const getActionButtons = () => {
    if (isMonitoring) {
      return data.answers?.[0]?.monitoringStatus !== 2 ? (
        <div className="flex gap-2">
          <Button onClick={report} outline className="flex-1">
            신고하기
          </Button>
          <Button className="flex-1" onClick={onCompleteInspection}>
            검수완료
          </Button>
        </div>
      ) : (
        <Button outline onClick={onCancelInspection}>
          검수취소
        </Button>
      );
    }

    if (!data.answers?.length) {
      return (
        <div className="flex gap-2">
          {isTransfer ? (
            <Button onClick={onBack} outline className="flex-1">
              목록
            </Button>
          ) : (
            <Button onClick={requestTransfer} outline className="flex-1">
              이관 신청
            </Button>
          )}
          {!isTransfer || user!.grade.id === 1 ? (
            <Button type="submit" className="flex-1">
              답변 등록
            </Button>
          ) : (
            <Button onClick={onBack} outline className="flex-1">
              목록
            </Button>
          )}
        </div>
      );
    }

    if (data.answers?.[0]?.monitoringStatus === 2) {
      return (
        <Button onClick={onBack} outline>
          목록
        </Button>
      );
    }

    if (myAnswer) {
      if (!data.enabled) {
        return (
          <Button outline onClick={onActivate}>
            활성
          </Button>
        );
      }

      return (
        <div className="space-y-2">
          <Button type="submit" className="w-full">
            답변 수정
          </Button>
          <div className="flex gap-2">
            <Button onClick={onDelete} outline className="flex-1">
              삭제
            </Button>
            <Button color="lightgray" className="flex-1" onClick={onDeactivate}>
              비활성화
            </Button>
          </div>
        </div>
      );
    }

    return (
      <Button onClick={onBack} outline>
        목록
      </Button>
    );
  };

  return (
    <div className="sticky overflow-auto top-[9.563rem] h-[calc(100vh-11.438rem)] flex flex-col w-[18rem] 1400:w-[19rem] 1500:w-[21.875rem] 1700:w-[31.813rem] space-y-[0.438rem]">
      <Box className="w-full space-y-5">
        <h4 className="font-medium text-18">
          {data.student?.studentName} ({data.student?.studentId})
        </h4>
        <dl className="grid grid-cols-[5.313rem_1fr] gap-y-[0.625rem] text-14">
          <dt className="text-secondary">학년</dt>
          <dd>{data.student?.grade}</dd>
          <dt className="text-secondary">계열</dt>
          <dd>{data.student?.department}</dd>
          <dt className="text-secondary">과목등급 </dt>
          <dd>{data.student?.subjectLevel}</dd>
          <dt className="text-secondary">메모</dt>
          <dd className="inline-flex">
            <textarea
              {...registration}
              onBlur={onBlur}
              className="resize-none rounded-md max-w-[14.063rem] px-[0.625rem] py-[0.313rem] border border-secondary w-full h-[3.563rem] text-12 leading-[0.938rem]"
            />
          </dd>
        </dl>
      </Box>
      <Box className="flex-1 overflow-hidden flex flex-col !p-0">
        {["대기중", "작성중"].includes(
          parseSearchString(location.search).status,
        ) ? (
          <QnaList />
        ) : isTransfer ? (
          <div className="p-5 space-y-5 flex flex-col overflow-hidden">
            <h4 className="font-medium text-18">이관 사유</h4>
            <dl className="overflow-auto text-14 grid grid-cols-[5.313rem_1fr] gap-y-[0.625rem]">
              <dt className="text-secondary">이관자</dt>
              <dd>
                {data.transfer?.user?.name}({data.transfer?.user?.userId})
              </dd>
              <dt className="text-secondary">등급</dt>
              <dd>{data.transfer?.user?.grade?.name}</dd>
              <dt className="text-secondary">이관일</dt>
              <dd>{format(data.transfer?.createdAt, "yyyy/MM/dd")}</dd>
              <dt className="text-secondary">이관 사유</dt>
              <dd className="whitespace-pre-wrap">
                <div
                  className="leading-normal"
                  dangerouslySetInnerHTML={{
                    __html: data.transfer?.content || "",
                  }}
                />
              </dd>
              <dt className="text-secondary">첨부파일</dt>
              <dd>
                <TransferImages files={data.transfer?.files} />
              </dd>
            </dl>
          </div>
        ) : (
          <>
            <h4 className="font-medium text-18 px-5 pt-[1.063rem]">
              조언 기록
            </h4>
            <div className="flex-1 overflow-hidden pt-[1.063rem] flex flex-col">
              <ul className="flex-1 overflow-auto text-14">
                {data.advice?.length ? (
                  data.advice.map((advice) => (
                    <li
                      key={advice.id}
                      className="relative p-5 flex flex-col pt-[0.625rem] bg-[#F5F5F5]"
                    >
                      {user?.id === advice.user?.id && (
                        <button
                          onClick={() => deleteAdvice(advice.id)}
                          type="button"
                          className="absolute right-5 top-[0.375rem] z-10"
                        >
                          <TrashIcon />
                        </button>
                      )}
                      <strong className="font-medium leading-4 mb-[0.625rem]">
                        {advice.user?.name} ({advice.user?.userId})
                      </strong>
                      <span className="text-secondary leading-[0.688rem] mb-5">
                        {format(advice.createdAt, "yyyy-MM-dd HH:mm:ss")}
                      </span>
                      <p className="whitespace-pre-wrap leading-5">
                        {advice.content}
                      </p>
                    </li>
                  ))
                ) : (
                  <p className="text-14 text-secondary p-5">
                    작성된 기록이 없습니다.
                  </p>
                )}
              </ul>
              {isMonitoring && (
                <div className="p-5 border-t border-gray">
                  <div className="flex flex-col border rounded-md border-gray">
                    <textarea
                      {...register("advice")}
                      className="outline-none resize-none px-3 py-[0.438rem]"
                    ></textarea>
                    <button
                      type="button"
                      disabled={!advice?.length}
                      onClick={onWriteAdvice}
                      className="self-end w-[3.125rem] h-[1.875rem] inline-flex justify-center items-center rounded-[3.125rem] bg-[#EFF0F6] text-[#B4B5B5] mb-[0.625rem] mr-[0.625rem] text-12"
                    >
                      등록
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </Box>
      {getActionButtons()}
    </div>
  );
};

const StudentQuestions = () => {
  const params = useParams();
  const methods = useForm<{ page: number }>();

  const size = 10;
  const page = methods.watch("page");

  const { data, totalItems, currentPage } = useGetQuestions({
    page,
    size,
    questionBNo: params.id!,
  });

  if (!data?.length) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <hr className="border-[#A5A5A5] my-5" />
      <div className="w-full text-16 space-y-[0.313rem]">
        <div className="h-14 flex items-center bg-secondary text-white text-center">
          <span className="min-w-16 w-[9.5%] max-w-[7.188rem]">번호</span>
          <span className="min-w-16 w-[9.5%] max-w-[7.188rem]">상태</span>
          <span className="flex-1 text-left px-[3.75rem]">제목</span>
          <span className="min-w-16 w-[9.5%] max-w-[7.188rem]">질문 유형</span>
          <span className="min-w-16 w-[9.5%] max-w-[7.188rem]">작성자</span>
          <span className="min-w-16 w-[9.5%] max-w-[7.188rem]">답변자</span>
          <span className="min-w-16 w-[9.5%] max-w-[7.188rem]">답변일</span>
          <span className="min-w-14 w-[5.35%] max-w-[4.063rem]"></span>
        </div>
        <ul className="space-y-[0.313rem]">
          {data.map((question, index) => (
            <Item
              key={question.bNo}
              {...question}
              index={totalItems - index - size * (currentPage - 1)}
            />
          ))}
        </ul>
        <Pagination total={totalItems} pageSize={size} />
      </div>
    </FormProvider>
  );
};

const Item = (question: Question & { index: number }) => {
  const user = useRecoilValue(userState);
  const [height, setHeight] = useState<0 | "auto">(0);
  const maskUserId = useMaskUserId();

  const toggleHeight = () => {
    setHeight((h) => (h ? 0 : "auto"));
  };

  return (
    <li>
      <button
        type="button"
        onClick={toggleHeight}
        className="h-14 w-full flex items-center bg-white text-secondary"
      >
        <span className="min-w-16 w-[9.5%] max-w-[7.188rem]">
          {question.index}
        </span>
        <span className="min-w-16 w-[9.5%] max-w-[7.188rem]">
          <Chip
            color={
              question.status === "대기중"
                ? "red"
                : question.status === "답변 완료"
                  ? "blue"
                  : question.status === "작성중"
                    ? "gray"
                    : "orange"
            }
          >
            {question.status === "답변 완료" ? "완료" : question.status}
          </Chip>
        </span>
        <span className="px-[3.75rem] flex-1">
          <div className="flex items-center">
            {question.secret && <LockIcon className="size-6 shrink-0" />}
            <p className="text-left line-clamp-1 break-all">{question.title}</p>
          </div>
        </span>
        <span className="min-w-16 w-[9.5%] max-w-[7.188rem]">
          {question.questionType}
        </span>
        <div className="whitespace-pre inline-block min-w-16 w-[9.5%] max-w-[7.188rem]">
          <div className="flex flex-col items-center">
            <span>{question.student?.studentName}</span>
            <span className="text-12">{question.student?.studentId}</span>
          </div>
        </div>
        <div className="whitespace-pre inline-block min-w-16 w-[9.5%] max-w-[7.188rem]">
          {!!question.answers?.length && (
            <div className="flex flex-col items-center">
              <span>
                {question.answers[0].responderName ??
                  question.answers[0].user?.name}
              </span>
              <span className="text-12">
                {question.answers[0].responderId ||
                  maskUserId(question.answers[0].user?.userId)}
              </span>
            </div>
          )}
        </div>
        <span className="min-w-16 w-[9.5%] max-w-[7.188rem]">
          {format(question.answers?.[0]?.registrationDate, "yyyy/MM/dd")}
        </span>
        <span className="min-w-14 w-[5.35%] max-w-[4.063rem]">
          <span className="inline-block">
            <CaretDownIcon
              className={clsx("size-[0.875rem]", {
                "-rotate-180 duration-300": height === "auto",
              })}
            />
          </span>
        </span>
      </button>
      <AnimateHeight height={height} duration={300} className="text-left">
        <table className="w-full table-fixed border border-gray bg-white mt-[0.313rem]">
          <tbody>
            <tr className="h-14 border-b border-gray">
              <th
                scope="row"
                className="border-r border-gray w-[7rem] px-6 text-left bg-lightGray"
              >
                질문 유형
              </th>
              <td className="px-[1.875rem] w-[7.5rem] 1400:w-[8.125rem] 1500:w-[9.375rem] 1600:w-[10.938rem]">
                <p className="line-clamp-1">{question.questionType}</p>
              </td>
              <th
                scope="row"
                className="border-r border-gray w-[8.875rem] px-6 text-left bg-lightGray"
              >
                강좌수강유/무
              </th>
              <td className="px-[1.875rem] w-[9.375rem] 1600:w-[10.938rem]">
                <p className="line-clamp-1">{question.courseAttendance}</p>
              </td>
              <th
                scope="row"
                className="border-r border-gray w-[7rem] px-6 text-left bg-lightGray"
              >
                첨부파일
              </th>
              <td className="px-[1.875rem]">
                {!!question.files.length &&
                  question.files.map((file) => (
                    <FileUrl
                      key={file.fileno}
                      file={file}
                      files={question.files}
                    />
                  ))}
              </td>
            </tr>
            <tr className="border-b border-gray">
              <th
                scope="row"
                className="border-r border-gray pt-4 pb-[1.094rem] w-[7rem] pl-6 text-left align-top bg-lightGray"
              >
                강좌/강의
              </th>
              <td colSpan={5} className="px-[1.875rem] py-4">
                {question.courseName} {question.videoLink ? "동영상 위치" : ""}{" "}
                {question.videoTimestamp && (
                  <a
                    href={question.videoLink}
                    target="_blank noreferrer"
                    className="text-primary"
                  >
                    {question.videoTimestamp}
                  </a>
                )}
              </td>
            </tr>
            <tr className="border-b border-gray">
              <th
                scope="row"
                className="border-r border-gray pt-4 pb-[1.094rem] w-[7rem] px-6 text-left align-top bg-lightGray"
              >
                교재
              </th>
              <td colSpan={5} className="px-[1.875rem] py-4">
                {question.textbookName
                  ? `교재명 :${question.textbookName} 페이지수 :${question.textbookPage} 문제번호 :${question.problemNo ?? "미 기입"}`
                  : ""}
              </td>
            </tr>
            <tr>
              <th
                scope="row"
                className="border-r border-gray pt-4 pb-[1.094rem] w-[7rem] px-6 text-left align-top bg-lightGray"
              >
                질문 내용
              </th>
              <td
                colSpan={5}
                className="px-[1.875rem] py-4 whitespace-pre-wrap"
              >
                <div
                  className="leading-normal"
                  dangerouslySetInnerHTML={{
                    __html: question.questionContent || "",
                  }}
                />
              </td>
            </tr>
          </tbody>
        </table>
        {!!question.answers?.length &&
          question.answers.map((answer) => (
            <table
              key={answer.id}
              className="table-fixed w-full border border-gray mt-[0.313rem]"
            >
              <tbody>
                <tr className="border-b border-gray">
                  <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                    제목
                  </th>
                  <td className="bg-white pl-[1.875rem] pr-5">
                    {answer.title}
                  </td>
                </tr>
                <tr className="border-b border-gray">
                  <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                    답변자
                  </th>
                  <td className="bg-white pl-[1.875rem] pr-5">
                    <>
                      {`${answer.responderName ?? answer.user?.name}${answer.responderId ? `(${answer.responderId})` : `(${maskUserId(answer.user?.userId)})`}${answer.user ? " / " : ""}`}
                      {answer.user && [1, 2].includes(user!.grade.id) && (
                        <strong className="font-normal text-primary">
                          {answer.user.grade?.name}
                        </strong>
                      )}
                    </>
                  </td>
                </tr>
                <tr className="border-b border-gray">
                  <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                    질문 분류
                  </th>
                  <td className="bg-white px-[1.875rem]">
                    {answer.classificationType}{" "}
                    {answer.classificationDifficulty}
                  </td>
                </tr>
                <tr className="border-b border-gray">
                  <th className="w-[7rem] align-top pt-[0.938rem] bg-lightGray px-6 text-left text-16">
                    답변 내용
                  </th>
                  <td className="bg-white pl-[1.875rem] pr-5 pb-6 pt-[0.625rem] whitespace-pre-wrap">
                    <div
                      className="qna-answer leading-normal"
                      dangerouslySetInnerHTML={{
                        __html: answer.content || "",
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                    첨부파일
                  </th>
                  <td className="bg-white px-[1.875rem]">
                    {answer.files?.map((file) => (
                      <FileUrl
                        key={file.fileno}
                        file={file}
                        files={answer.files}
                      />
                    ))}
                  </td>
                </tr>
              </tbody>
            </table>
          ))}
      </AnimateHeight>
    </li>
  );
};

export default QnADetailPage;
